import React, { useState } from 'react';
import {
  TimePreset,
  AggregationLevel,
  TIME_PRESETS,
  AGGREGATION_OPTIONS,
  LiveModeSettings,
} from '../types';

interface TimeToolbarProps {
  timePreset: TimePreset;
  onTimePresetChange: (preset: TimePreset) => void;
  customFrom: Date | null;
  customTo: Date | null;
  onCustomRangeChange: (from: Date | null, to: Date | null) => void;
  aggregation: AggregationLevel;
  onAggregationChange: (level: AggregationLevel) => void;
  effectiveAggregation?: AggregationLevel | null; // The actual aggregation after auto-calculation
  liveMode: LiveModeSettings;
  onLiveModeChange: (settings: Partial<LiveModeSettings>) => void;
  lastUpdated: Date | null;
  totalCount: number;
  dataPointCount: number;
  isZoomed?: boolean; // Whether we're viewing a zoomed/selected range
  onResetZoom?: () => void; // Reset to original time preset
}

const toolbarStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    background: 'rgba(33, 40, 44, 0.8)',
    borderRadius: '8px',
    border: '1px solid rgba(108, 117, 125, 0.3)',
    marginBottom: '1rem',
  },
  section: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  label: {
    color: '#8b949e',
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  presetButton: {
    padding: '0.35rem 0.75rem',
    borderRadius: '4px',
    border: '1px solid rgba(108, 117, 125, 0.4)',
    background: 'rgba(33, 40, 44, 0.6)',
    color: '#c5c5c5',
    fontSize: '0.8rem',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  presetButtonActive: {
    background: 'rgba(83, 189, 228, 0.25)',
    borderColor: '#53bde4',
    color: '#53bde4',
  },
  select: {
    padding: '0.35rem 0.6rem',
    borderRadius: '4px',
    border: '1px solid rgba(108, 117, 125, 0.4)',
    background: 'rgba(33, 40, 44, 0.95)',
    color: '#c5c5c5',
    fontSize: '0.8rem',
    cursor: 'pointer',
  },
  dateInput: {
    padding: '0.35rem 0.5rem',
    borderRadius: '4px',
    border: '1px solid rgba(108, 117, 125, 0.4)',
    background: 'rgba(33, 40, 44, 0.95)',
    color: '#c5c5c5',
    fontSize: '0.8rem',
  },
  liveToggle: {
    padding: '0.35rem 0.75rem',
    borderRadius: '4px',
    border: '1px solid rgba(108, 117, 125, 0.4)',
    background: 'rgba(33, 40, 44, 0.6)',
    color: '#c5c5c5',
    fontSize: '0.8rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
  },
  liveToggleActive: {
    background: 'rgba(0, 255, 136, 0.15)',
    borderColor: '#00ff88',
    color: '#00ff88',
  },
  divider: {
    width: '1px',
    height: '24px',
    background: 'rgba(108, 117, 125, 0.3)',
  },
  stats: {
    marginLeft: 'auto',
    color: '#6c757d',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  statValue: {
    color: '#53bde4',
    fontWeight: 500,
  },
};

const TIME_PRESET_BUTTONS: TimePreset[] = ['1h', '6h', '24h', '7d', '30d', '90d', '1y', 'all'];

export function TimeToolbar({
  timePreset,
  onTimePresetChange,
  customFrom,
  customTo,
  onCustomRangeChange,
  aggregation,
  onAggregationChange,
  effectiveAggregation,
  liveMode,
  onLiveModeChange,
  lastUpdated,
  totalCount,
  dataPointCount,
  isZoomed = false,
  onResetZoom,
}: TimeToolbarProps) {
  const [showCustomRange, setShowCustomRange] = useState(timePreset === 'custom');

  const formatDateForInput = (date: Date | null): string => {
    if (!date) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const handlePresetClick = (preset: TimePreset) => {
    if (preset === 'custom') {
      setShowCustomRange(true);
    } else {
      setShowCustomRange(false);
    }
    onTimePresetChange(preset);
    // Disable live mode when changing preset
    if (liveMode.enabled) {
      onLiveModeChange({ enabled: false });
    }
  };

  const handleCustomFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value ? new Date(e.target.value) : null;
    onCustomRangeChange(date, customTo);
  };

  const handleCustomToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value ? new Date(e.target.value) : null;
    onCustomRangeChange(customFrom, date);
  };

  const toggleLiveMode = () => {
    onLiveModeChange({ enabled: !liveMode.enabled });
  };

  return (
    <div style={toolbarStyles.container}>
      {/* Time presets */}
      <div style={toolbarStyles.section}>
        <span style={toolbarStyles.label}>Time</span>
        {TIME_PRESET_BUTTONS.map(preset => (
          <button
            key={preset}
            onClick={() => handlePresetClick(preset)}
            style={{
              ...toolbarStyles.presetButton,
              ...(timePreset === preset ? toolbarStyles.presetButtonActive : {}),
            }}
          >
            {TIME_PRESETS[preset].label.replace('Last ', '')}
          </button>
        ))}
        <button
          onClick={() => handlePresetClick('custom')}
          style={{
            ...toolbarStyles.presetButton,
            ...(timePreset === 'custom' ? toolbarStyles.presetButtonActive : {}),
          }}
        >
          Custom
        </button>
      </div>

      {/* Custom date range */}
      {showCustomRange && (
        <>
          <div style={toolbarStyles.divider} />
          <div style={toolbarStyles.section}>
            <input
              type="datetime-local"
              value={formatDateForInput(customFrom)}
              onChange={handleCustomFromChange}
              style={toolbarStyles.dateInput}
            />
            <span style={{ color: '#6c757d' }}>to</span>
            <input
              type="datetime-local"
              value={formatDateForInput(customTo)}
              onChange={handleCustomToChange}
              style={toolbarStyles.dateInput}
            />
          </div>
        </>
      )}

      <div style={toolbarStyles.divider} />

      {/* Aggregation */}
      <div style={toolbarStyles.section}>
        <span style={toolbarStyles.label}>Bucket</span>
        <select
          value={aggregation}
          onChange={e => onAggregationChange(e.target.value as AggregationLevel)}
          style={toolbarStyles.select}
        >
          {AGGREGATION_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {/* Show effective aggregation when using auto mode */}
        {aggregation === 'raw' && effectiveAggregation && effectiveAggregation !== 'raw' && (
          <span style={{ color: '#53bde4', fontSize: '0.75rem' }}>
            → {effectiveAggregation}
          </span>
        )}
      </div>

      {/* Zoom indicator and reset */}
      {isZoomed && onResetZoom && (
        <button
          onClick={onResetZoom}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.35rem',
            padding: '0.4rem 0.75rem',
            borderRadius: '4px',
            border: '1px solid rgba(255, 152, 0, 0.5)',
            background: 'rgba(255, 152, 0, 0.15)',
            color: '#ff9800',
            fontSize: '0.8rem',
            cursor: 'pointer',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
          Reset Zoom
        </button>
      )}

      <div style={toolbarStyles.divider} />

      {/* Live mode toggle */}
      <button
        onClick={toggleLiveMode}
        style={{
          ...toolbarStyles.liveToggle,
          ...(liveMode.enabled ? toolbarStyles.liveToggleActive : {}),
        }}
      >
        <span style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: liveMode.enabled ? '#00ff88' : '#6c757d',
          animation: liveMode.enabled ? 'pulse 1.5s infinite' : 'none',
        }} />
        Live
      </button>

      {liveMode.enabled && (
        <select
          value={liveMode.intervalMs}
          onChange={e => onLiveModeChange({ intervalMs: Number(e.target.value) })}
          style={toolbarStyles.select}
        >
          <option value={1000}>1s</option>
          <option value={5000}>5s</option>
          <option value={10000}>10s</option>
          <option value={30000}>30s</option>
          <option value={60000}>1m</option>
        </select>
      )}

      {/* Stats */}
      <div style={toolbarStyles.stats}>
        <span>
          Points: <span style={toolbarStyles.statValue}>{dataPointCount.toLocaleString()}</span>
          {totalCount > dataPointCount && (
            <span> / {totalCount.toLocaleString()}</span>
          )}
        </span>
        {lastUpdated && (
          <span>
            Updated: <span style={toolbarStyles.statValue}>
              {lastUpdated.toLocaleTimeString()}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

export default TimeToolbar;
